
import _ from "lodash";

import { DebugEx } from "@Utils/DebugEx"
import { isDefined, isFunction, IsNullOrEmpty, isObject, isStringEmpty, RunSafe } from '@Utils/Micro';
import { WebApps } from '@ModelViews/ModelViews';
import { addDocumentFocus } from '@Utils/Utils';
import { Plegma } from './Api';
import { PlegmaStatics } from './PlegmaStatics';

// =======================================================================================================
export interface YodiwoDomains {
    Cyan: string;
    //CyanWebSocket: string;
    WebSocket: string;
    //Mqtt: string;
    //UploadUrl: string;
    //UploadFromUriUrl: string;
    //filePostUrl: string;
}
// -------------------------------------------------------------
declare var domains: YodiwoDomains;
export function getDomains(): YodiwoDomains {
    return domains;
}
// =======================================================================================================
export interface Keys {
    nodeKey: string;
    secretKey: string;
    token1: string;
    token2: string;
}
// -------------------------------------------------------------
export interface ApplicationInfo {
    name: string;
    description: string;
}
// -------------------------------------------------------------
export interface WSConf {
    // Define that we are not going to gave actual ws connection
    dummyWS: boolean;

    // Nodekey that contain and the server id 
    NodeKey: string;
    BoardId: string;

    // Server information
    ServerUrl: string;
    WebSocketUrl: string;

    // Application information
    ApplicationName: string;
    ApplicationUUID: string;
    ApplicationDescription: string;
    ApplicationImage: string;

    // Timeout Mechanism
    Timeout?: number;

    // Predefined keys to bypass the pairing
    Keys?: Keys;

    // User info, If we set this we sending the user information to cyan
    UserInfo?: WebApps.UserInfoView;

    // Connection parameters / flags
    Flags?: Plegma.eConnectionFlags;
    NodeType?: Plegma.eNodeType;

    // The things that this node is support
    ThingTypes?: Plegma.ThingType[];

    AdditionalInfo?: { [key: string]: string };
}
// -------------------------------------------------------------
export enum ErrorType {
    Undefined = 0,
    API = 1,
    Pairing = 2,
    Connection = 3,
    Msg = 4
}
// -------------------------------------------------------------
export enum ErrorCode {
    Undefined = 0,
    Configuration = 1,
    NotPaired = 2,
    NotConnected = 3,
    MsgRx = 4,
    MsgTx = 5,
    InvalidKeys = 6
}
// =======================================================================================================
export class WS {
    // --------------------------------------------------------------------------------------------------
    // Internal variables

    private conf: WSConf;
    private things: Plegma.Thing[];
    private syncReqId: number;
    private syncPortEventReqId: number;
    private syncPortEventRespId: number;
    private timerSyncPortCheck: any;
    private syncWarlockReqId: number;

    private syncReqTimestamps: { id: number, timestamp: number }[] = [];

    private socket: WebSocket;

    private activePortKeys: string[];
    private syncReqCallbacks: { [id: number]: { callback: (rsp: any) => void } } = {};
    // --------------------------------------------------------------------------------------------------
    // Public variables

    public isPaired: boolean;
    public sendTokensOnPairingRetry: boolean;
    public ThingsRevNum: number;
    public get BoardId(): string | null { return this.conf.BoardId; }
    public get CyanNodeKey(): string | null { return this.conf.Keys.nodeKey; }
    public get NodeKey(): string | null { return this.conf.NodeKey; }
    public get WebsocketUrl(): string | null { return this.conf.WebSocketUrl; }
    public get UserInfo(): WebApps.UserInfoView | null { return isDefined(this.conf.UserInfo) ? this.conf.UserInfo : { Email: "", Domain: "", FirstName: "", LastName: "", AvatarUri: null }; }

    public onConnected: (nodeKey: string) => void;
    private onConnectedCalled: boolean;
    public onError: (error: ErrorType, ecode: ErrorCode, msg: any) => void;

    public onKeysUpdate: (keys: Keys, pairingURL: string) => void;
    public onUnpaired: (reasonCode: Plegma.eUnpairReason, message: string) => void;

    public onThingCreate: (thing: Plegma.Thing) => void;
    public onThingUpdate: (thing: Plegma.Thing) => void;
    public onThingDelete: (thingKey: string) => void;

    public onPortEvents: (events: Plegma.PortEvent[]) => void;
    public onPortStateGet: (operation: Plegma.ePortStateOperation, portKeys: string[]) => Plegma.PortState[];
    public onPortStateSet: (newStates: Plegma.PortState[]) => void;

    public onActivePortKeys: (portkeys: string[]) => void;
    public onAssignedEndpoint: (key: string) => void;

    public onGenericRsp: (status: number) => void;

    // --------------------------------------------------------------------------------------------------
    constructor(conf: WSConf, things: Plegma.Thing[]) {
        this.conf = conf;
        this.isPaired = false;
        this.sendTokensOnPairingRetry = true;
        this.onConnectedCalled = false;
        this.things = things;
        this.syncReqId = 0;
        this.syncPortEventReqId = 1;
        this.ThingsRevNum = 0;
        this.activePortKeys = null;
        this.syncWarlockReqId = 1;


        // Init focus
        if (isDefined(conf.Timeout) && conf.Timeout > 0)
            addDocumentFocus((isVisible) => this.enablePortEventSeq(isVisible));
    }
    // --------------------------------------------------------------------------------------------------
    private periodicCheckPortEventSeq = () => {
        if (this.isPaired && this.syncReqTimestamps.length > 0) {
            // find the older timstamp
            let diffTime = Date.now() - _.minBy(this.syncReqTimestamps, "timestamp").timestamp;
            // Check that the received sequence id is equal with the send one
            if ((this.syncPortEventReqId != this.syncPortEventRespId) &&
                (diffTime > this.conf.Timeout)) {
                DebugEx.TraceError("Triggered timeout:", diffTime, this.syncPortEventReqId, this.syncPortEventRespId)

                // Clean old timestamps to avoid any loops
                this.syncReqTimestamps = [];

                // Detected command timeout disconnect and have the system to recover it self
                this.socket.close();
            }
        }
    }
    // --------------------------------------------------------------------------------------------------
    private enablePortEventSeq(isVisible: boolean) {
        if (isDefined(this.timerSyncPortCheck))
            clearInterval(this.timerSyncPortCheck);
        if (isVisible) {
            DebugEx.TraceLog("Enable periodic port event timeout");
            this.timerSyncPortCheck = setInterval(this.periodicCheckPortEventSeq, 1000);
        }
    }
    // --------------------------------------------------------------------------------------------------
    private getNewPortEventSeqId() {
        let newId = ++this.syncPortEventReqId;
        this.syncReqTimestamps.push({ id: newId, timestamp: Date.now() });
        return newId;
    }
    // --------------------------------------------------------------------------------------------------
    private getNewSyncId(callback) {
        if (isDefined(callback)) {
            let newId = ++this.syncWarlockReqId;
            this.syncReqCallbacks[newId] = { callback: callback };
            return newId;
        } else
            return 0;
    }
    // ==================================================================================================
    // Internal Utils
    // ==================================================================================================
    private checkConnection() {
        if (this.socket == null || this.socket.readyState == 3) {
            this.Start();
        }
    }
    // --------------------------------------------------------------------------------------------------
    private reportError(error: ErrorType, ecode: ErrorCode, msg: any): void {
        if (isDefined(this.onError))
            this.onError(error, ecode, msg);
    }
    // --------------------------------------------------------------------------------------------------
    private sendMsg(Id: Plegma.eWSMsgId,
        SubId: string,
        Payload: any,
        SyncId?: number,
        Flags?: Plegma.eMsgFlags) {

        if (this.conf.dummyWS)
            return;

        // The payload must be in json form
        const jsonPayload = JSON.stringify(Payload);

        const msg: Plegma.WebSocketMsg = {
            Id: Id,
            SubId: SubId,
            Payload: jsonPayload,
            PayloadSize: jsonPayload.length,
            Flags: (isDefined(Flags)) ? Flags : Plegma.eMsgFlags.None,
            SyncId: (isDefined(SyncId)) ? SyncId : 0,
        };

        if (this.socket != null && this.socket.readyState == 1) {
            DebugEx.TraceLog('Browser->Cloud:', msg);

            this.socket.send(JSON.stringify(msg));
        } else {
            if (this.socket.readyState == 3)
                this.reportError(ErrorType.Connection, ErrorCode.NotConnected, "Lost connection and event");
        }
    }
    // --------------------------------------------------------------------------------------------------
    private createNodeInfoRspPayload(): Plegma.NodeInfoRsp {
        return {
            SeqNo: 0,
            Type: isDefined(this.conf.NodeType) ? this.conf.NodeType : Plegma.eNodeType.Dashboard,
            Name: this.conf.ApplicationName,
            Capabilities: Plegma.eNodeCapa.IsWarlock,
            ThingTypes: this.conf.ThingTypes,
            ThingsRevNum: this.ThingsRevNum,
            BlockLibraries: [],
            SupportedApiRev: PlegmaStatics.PlegmaAPI.APIVersion,
            AdditionalInfo: this.conf.AdditionalInfo,
            Restrictions: Plegma.eEndpointRestrictions.SkipPortStateSync | Plegma.eEndpointRestrictions.SkipThingsSync
        };
    }
    // --------------------------------------------------------------------------------------------------

    // ==================================================================================================
    // Handling events
    // ==================================================================================================
    private handleThingsGetSet(data: Plegma.ThingsGet, syncId: number, isResponse: boolean,
        operation: Plegma.eThingsOperation, hierarchy: string = undefined): void {
        var payload: Plegma.ThingsSet = {
            SeqNo: (isResponse) ? 0 : this.getNewPortEventSeqId(),
            Status: true,
            RevNum: this.ThingsRevNum,
            Data: [],
            Hierarchy: hierarchy,

            // If we requested only one thing then just update the thing to the server,
            // otherwise Overwrite the things that the server have
            Operation: operation,
        };

        // Fill data
        if (this.things != null) {
            // if the cloud have requested specific thing find it
            let thingId = (data != null && data.ThingKey != null) ? data.ThingKey.replace(this.conf.Keys.nodeKey + '-', "") : null;
            if (thingId != null) {
                let t = this.things.find((thing, index) => thing.ThingKey == thingId);
                if (t != null) {
                    let tNew = _.cloneDeep(t);
                    // Update the thing with nodekey
                    tNew.ThingKey = this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + t.ThingKey;
                    _.forEach(tNew.Ports, (port) => {
                        port.PortKey = tNew.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + port.PortKey;

                        // Remove state from the ports
                        delete port.State;
                    });
                    payload.Data.push(tNew);
                }
            } else {
                // Update and send all things
                _.forEach(this.things, (thing) => {
                    let tNew = _.cloneDeep(thing);
                    // Update the thing with nodekey
                    tNew.ThingKey = this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + thing.ThingKey;
                    _.forEach(tNew.Ports, (port) => {
                        port.PortKey = tNew.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + port.PortKey;

                        // Remove state from the ports
                        delete port.State;
                    });
                    payload.Data.push(tNew);
                });
            }
        }

        // Send the message
        this.sendMsg(
            Plegma.eWSMsgId.Api,
            PlegmaStatics.PlegmaAPI.s_ThingsSet,
            payload,
            syncId,
            (isResponse) ? Plegma.eMsgFlags.Response : Plegma.eMsgFlags.Request
        );

    }
    // --------------------------------------------------------------------------------------------------
    private handleUnknownReq(SyncId: number) {
        this.sendMsg(
            Plegma.eWSMsgId.Api,
            "UnknownRsp", // PlegmaStatics.PlegmaAPI., ???? TODO: Fill something here
            null,
            SyncId,
            Plegma.eMsgFlags.Response
        );
    }
    // --------------------------------------------------------------------------------------------------
    private handleThingSet(data: Plegma.ThingsSet, SyncId: number) {

        // Handle Update or Overwrite
        if ((data.Operation == Plegma.eThingsOperation.Update && isFunction(this.onThingUpdate)) ||
            (data.Operation == Plegma.eThingsOperation.Overwrite && isFunction(this.onThingCreate)) ||
            (data.Operation == Plegma.eThingsOperation.Delete && isFunction(this.onThingDelete))) {

            // Pass all things
            _.forEach(data.Data, (origThing) => {
                let thing = _.cloneDeep(origThing);

                // Update thing and node ports by remove nodekey
                thing.ThingKey = thing.ThingKey.replace(this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator, "");
                _.forEach(thing.Ports, (port) => {
                    port.PortKey = port.PortKey.replace(origThing.ThingKey + '-', "");
                });

                // Send the request to app
                if (data.Operation == Plegma.eThingsOperation.Update && isFunction(this.onThingUpdate))
                    this.onThingUpdate(thing);

                if (data.Operation == Plegma.eThingsOperation.Overwrite && isFunction(this.onThingCreate))
                    this.onThingCreate(thing);

                if (data.Operation == Plegma.eThingsOperation.Delete && isFunction(this.onThingDelete))
                    this.onThingDelete(thing.ThingKey);
            });
        }

        // Send responce
        let respPayload: Plegma.GenericRsp = {
            SeqNo: 0,
            IsSuccess: true,
            Message: "",
            StatusCode: 0
        };

        this.sendMsg(
            Plegma.eWSMsgId.Api,
            PlegmaStatics.PlegmaAPI.s_GenericRsp,
            respPayload,
            SyncId,
            Plegma.eMsgFlags.Response
        );
    }
    // --------------------------------------------------------------------------------------------------
    private handlePortEvent(event: Plegma.PortEventMsg) {
        if (isFunction(this.onPortEvents)) {
            _.forEach(event.PortEvents, (e) => {
                var partials = e.PortKey.split("-");
                if (partials[2] != null && partials[3] != null) {
                    e.PortKey = partials[2] + '-' + partials[3];
                }
            });

            // Call for all events
            this.onPortEvents(event.PortEvents);
        }
    }
    // --------------------------------------------------------------------------------------------------
    private handleActivePortKeys(event: Plegma.ActivePortKeysMsg) {
        // Clean old active keys
        this.activePortKeys = [];

        // Copy the new ones
        if (event.ActivePortKeys.length > 0) {
            this.activePortKeys = _.cloneDeep(event.ActivePortKeys);
        }

        // call user
        if (isFunction(this.onActivePortKeys)) {
            this.onActivePortKeys(this.activePortKeys);
        }
    }
    // --------------------------------------------------------------------------------------------------
    private handlePortStateGet(event: Plegma.PortStateGet, SyncId: number) {
        DebugEx.TraceVerbose("handle port state request");
        if (isDefined(this.onPortStateGet)) {
            let portStates: Plegma.PortState[];
            // Get states
            if (event.PortKeys != null && event.Operation != Plegma.ePortStateOperation.AllPortStates) {
                // Remove node key
                let ports = event.PortKeys.map((e) => e.replace(this.conf.Keys.nodeKey + '-', ""));
                portStates = this.onPortStateGet(event.Operation, ports);
            } else {
                portStates = this.onPortStateGet(event.Operation, null);
            }
            let respPayload: Plegma.PortStateSet = {
                Operation: event.Operation,
                SeqNo: 0,
                PortStates: portStates
            };
            this.sendMsg(
                Plegma.eWSMsgId.Api,
                PlegmaStatics.PlegmaAPI.s_PortStateSet,
                respPayload,
                SyncId,
                Plegma.eMsgFlags.Response
            );
        }
    }
    // --------------------------------------------------------------------------------------------------
    private handlePortStateSet(event: Plegma.PortStateSet, SyncId: number) {

        var newstates: Plegma.PortState[] = [];

        // Pass all things
        _.forEach(this.things, (thing) => {
            var thingKey = this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + thing.ThingKey;

            // Pass all ports of the thing
            _.forEach(thing.Ports, (port) => {
                var portKey = thingKey + PlegmaStatics.PlegmaAPI.KeySeparator + port.PortKey;

                // Pass  all port of event
                _.forEach(event.PortStates, (e) => {
                    // Find the one we are searching for
                    if (portKey == e.PortKey) {
                        let state: Plegma.PortState = _.cloneDeep(e);
                        state.PortKey = e.PortKey.replace(this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator, "");
                        newstates.push(state);
                    }
                });
            });
        });


        let success = false;
        if (newstates.length > 0 &&
            isFunction(this.onPortStateSet)) {
            this.onPortStateSet(newstates);
            success = true;
        }

        //This can be Cloud's response to our PortStateGet, or a new Cloud Request in which case we need to response with a GenericRsp
        //Check syncId to see which of the two this is and respond accordingly
        if (SyncId != 0) {

            // Send GenericRsp
            let respPayload: Plegma.GenericRsp = {
                SeqNo: 0,
                IsSuccess: success,
                Message: "",
                StatusCode: 0
            };

            this.sendMsg(
                Plegma.eWSMsgId.Api,
                PlegmaStatics.PlegmaAPI.s_GenericRsp,
                respPayload,
                SyncId,
                Plegma.eMsgFlags.Response
            );
        }
    }
    // --------------------------------------------------------------------------------------------------
    private handleGenericResp(resp: Plegma.GenericRsp) {
        if (resp.StatusCode == Plegma.eThingSolveErrorCode.RejectedDueToQuota) {
            DebugEx.TraceError("Event rejected due to quota");
            // TODO: handling rejections due to quota, show message

            if (resp.OrigSeqNo > 0)
                this.syncPortEventRespId = resp.OrigSeqNo;

            // clean all responds !!!! we don't care if we miss anything
            this.syncReqTimestamps = [];
        } else if (
            resp.StatusCode == Plegma.eThingSolveErrorCode.Forwarded ||
            resp.StatusCode == Plegma.eThingSolveErrorCode.Received
        ) {
            if (resp.OrigSeqNo > 0)
                this.syncPortEventRespId = resp.OrigSeqNo;

            // clean all responds !!!! we don't care if we miss anything
            this.syncReqTimestamps = [];
        }

        if (isFunction(this.onGenericRsp))
            this.onGenericRsp(resp.StatusCode);
    }
    // --------------------------------------------------------------------------------------------------
    private handleWarlockResp(resp: any, wsRequest: Plegma.WebSocketMsg) {
        const syncId = wsRequest.SyncId;
        if (!isDefined(syncId) || wsRequest.Flags != Plegma.eMsgFlags.Response)
            return;

        //try get corresponding cached entry 
        const cacheEntry = this.syncReqCallbacks[syncId];
        if (isDefined(cacheEntry)) {
            if (isFunction(cacheEntry.callback)) {
                RunSafe(() => cacheEntry.callback(resp));
            }
            // remove entry from cache
            delete this.syncReqCallbacks[syncId];
        }
    }
    // --------------------------------------------------------------------------------------------------
    private sendGetKeysRequest(): boolean {
        // If we are not have the secret key but we have the token
        // we must finish the pairing by sending the pairing request in websocket
        if (this.conf.Keys.token1 != null && this.conf.Keys.token2 != null) {

            // Send ket keys request
            let payload: Plegma.PairingNodeGetKeysRequest = {
                token1: this.conf.Keys.token1,
                token2: this.conf.Keys.token2
            };

            this.sendMsg(
                Plegma.eWSMsgId.Pairing,
                PlegmaStatics.NodePairingConstants.s_GetKeysRequest,
                payload);

            return true;
        }
        return false;
    }
    // --------------------------------------------------------------------------------------------------
    public applicationInfoChanged(appInfo: ApplicationInfo): void {
        // Update application's name and description.
        this.conf.ApplicationName = appInfo.name;
        this.conf.ApplicationDescription = appInfo.description;

        // Send new node's info to WS server.
        this.sendMsg(
            Plegma.eWSMsgId.Api,
            PlegmaStatics.PlegmaAPI.s_NodeInfoRsp,
            this.createNodeInfoRspPayload()
        );
    }
    // --------------------------------------------------------------------------------------------------

    // ==================================================================================================
    // Public API
    // ==================================================================================================
    public Start() {

        // Start Connection and set the cb events
        this.socket = new WebSocket(this.conf.WebSocketUrl);

        // ---------------------------------------------------- On Open
        this.socket.onopen = (evt) => {
            if (isDefined(this.conf.Keys)) {
                // If we have the keys then we are connected ??
                // Cloud is going to ask us for the login
                if (isDefined(this.conf.Keys.secretKey)) {
                    return;
                }

                if (this.sendTokensOnPairingRetry) {
                    if (this.sendGetKeysRequest())
                        return;
                }
                else {
                    this.sendTokensOnPairingRetry = true;
                    return;
                }
            }

            // We don't have secret key or have any pending pairing
            this.reportError(ErrorType.Pairing, ErrorCode.NotPaired, null);
        };
        // ---------------------------------------------------- On Close
        this.socket.onclose = (evt) => {
            if (evt.reason == "Invalid Node Login") // TODO: Move the sting to Plegma
            {
                // Remove any keys
                this.conf.Keys = undefined;
                this.isPaired = false;
                this.onConnectedCalled = false;

                this.reportError(ErrorType.Connection, ErrorCode.InvalidKeys, evt);
                return;
            }
            else if (isDefined(this.conf.Keys) &&
                evt.reason == 'pairing context token2=' + this.conf.Keys.token2 + ' does not have a userkey yet') {
                this.sendTokensOnPairingRetry = false;
            }

            this.reportError(ErrorType.Connection, ErrorCode.NotConnected, evt);
        };
        // ---------------------------------------------------- On Error
        this.socket.onerror = (evt) => {
            this.reportError(ErrorType.Undefined, ErrorCode.Undefined, evt);
        };
        // ---------------------------------------------------- On Message
        this.socket.onmessage = (evt) => {
            let wsRequest: Plegma.WebSocketMsg = null;
            let reqPayload: any = null;
            try {
                // parse request
                wsRequest = JSON.parse(evt.data);
            } catch (ex) {
                DebugEx.TraceError("Failed to parse WebSocketMsg", ex);
                return;
            }

            // Avoid empty requests
            if (wsRequest == null) {
                return;
            }

            try {
                // parse payload
                reqPayload = JSON.parse(wsRequest.Payload);
            } catch (ex) {
                DebugEx.TraceError("Failed to parse WebSocketMsg->payload", ex);
                return;
            }

            DebugEx.TraceLog('Cloud->Browser:', wsRequest, reqPayload);

            try {
                // handling the different requests
                switch (wsRequest.Id) {

                    // ------------------------------------
                    case Plegma.eWSMsgId.Pairing: {
                        switch (wsRequest.SubId) {
                            // ****************************
                            case PlegmaStatics.NodePairingConstants.s_TokensResponse: {
                                const req: Plegma.PairingServerTokensResponse = reqPayload;

                                // Save tokens
                                this.conf.Keys = {
                                    nodeKey: null,
                                    secretKey: null,
                                    token1: req.token1,
                                    token2: req.token2
                                };

                                // Redirect for pairing
                                const rurl = this.conf.ServerUrl + '/pairing/1/userconfirm?' + 'token2=' + req.token2 + '&uuid=' + this.conf.ApplicationUUID + '&noderedirect=' + window.location.href;

                                // report to the client the pairing
                                if (isDefined(this.onKeysUpdate))
                                    this.onKeysUpdate(this.conf.Keys, rurl);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.NodePairingConstants.s_KeysResponse: {
                                const req: Plegma.PairingServerKeysResponse = reqPayload;

                                // Update keys
                                this.conf.Keys = {
                                    nodeKey: req.nodeKey,
                                    secretKey: req.secretKey,
                                    token1: null,
                                    token2: null
                                };

                                // report to the client the pairing
                                if (isDefined(this.onKeysUpdate))
                                    this.onKeysUpdate(this.conf.Keys, null);
                                break;
                            }
                            // ****************************
                        }
                        break;
                    } //case Plegma.eWSMsgId.Pairing

                    // ------------------------------------
                    case Plegma.eWSMsgId.Api: {
                        switch (wsRequest.SubId) {
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_LoginReq: {
                                if (isDefined(this.conf.Keys)) {
                                    if (!isStringEmpty(this.conf.Keys.secretKey)) {
                                        let respPayload: Plegma.LoginRsp = {
                                            SeqNo: 0,
                                            Flags: this.conf.Flags,
                                            NodeKey: this.conf.Keys.nodeKey,
                                            SecretKey: this.conf.Keys.secretKey,
                                            DesiredEndpoint: null
                                        };

                                        this.sendMsg(
                                            Plegma.eWSMsgId.Api,
                                            PlegmaStatics.PlegmaAPI.s_LoginRsp,
                                            respPayload,
                                            wsRequest.SyncId,
                                            Plegma.eMsgFlags.Response
                                        );
                                    }
                                }
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_NodeInfoReq: {
                                const req: Plegma.NodeInfoReq = reqPayload;

                                // Get the setup revision number
                                this.ThingsRevNum = req.ThingsRevNum;

                                // Report the Active key to system
                                if (isDefined(this.onAssignedEndpoint))
                                    this.onAssignedEndpoint(req.AssignedEndpoint);

                                this.sendMsg(
                                    Plegma.eWSMsgId.Api,
                                    PlegmaStatics.PlegmaAPI.s_NodeInfoRsp,
                                    this.createNodeInfoRspPayload(),
                                    wsRequest.SyncId,
                                    Plegma.eMsgFlags.Response
                                );

                                // If we get active portkey msg we are connected
                                this.isPaired = true;

                                // We assume that when we get active port keys we are connected
                                if (isDefined(this.onConnected) && !this.onConnectedCalled) {
                                    this.onConnected(this.conf.Keys.nodeKey);
                                    this.onConnectedCalled = true;
                                }
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_NodeUnpairedReq: {
                                const req: Plegma.NodeUnpairedReq = reqPayload;

                                if (isDefined(this.onUnpaired))
                                    this.onUnpaired(req.ReasonCode, req.Message);

                                // Clean keys
                                this.conf.Keys = undefined;
                                this.isPaired = false;

                                this.conf.ApplicationName = this.conf.ApplicationDescription = 'Dashboard';

                                let respPayload: Plegma.GenericRsp = {
                                    SeqNo: 0,
                                    IsSuccess: true,
                                    Message: "",
                                    StatusCode: 0
                                };

                                this.sendMsg(
                                    Plegma.eWSMsgId.Api,
                                    PlegmaStatics.PlegmaAPI.s_GenericRsp,
                                    respPayload,
                                    wsRequest.SyncId,
                                    Plegma.eMsgFlags.Response
                                );
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_ThingsGet: {
                                const req: Plegma.ThingsGet = reqPayload;

                                if (req.Operation == Plegma.eThingsOperation.Get) {
                                    this.handleThingsGetSet(req, wsRequest.SyncId, true, Plegma.eThingsOperation.Update);
                                } else {
                                    this.handleUnknownReq(wsRequest.SyncId);
                                }
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_ThingsSet: {
                                const req: Plegma.ThingsSet = reqPayload;
                                this.handleThingSet(req, wsRequest.SyncId);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_PortEventMsg: {
                                const req: Plegma.PortEventMsg = reqPayload;
                                this.handlePortEvent(req);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_ActivePortKeysMsg: {
                                const req: Plegma.ActivePortKeysMsg = reqPayload;

                                this.handleActivePortKeys(req);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_PortStateGet: {
                                const req: Plegma.PortStateGet = reqPayload;
                                this.handlePortStateGet(req, wsRequest.SyncId);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_PortStateSet: {
                                const req: Plegma.PortStateSet = reqPayload;
                                this.handlePortStateSet(req, wsRequest.SyncId);
                                break;
                            }
                            // ****************************
                            case PlegmaStatics.PlegmaAPI.s_GenericRsp: {
                                if (this._WarlockWorkAround(wsRequest))
                                    // Temporary fix. Request id need to be type "Warlock"
                                    this.handleWarlockResp(reqPayload, wsRequest);
                                else
                                    this.handleGenericResp(reqPayload as Plegma.GenericRsp);
                                break;
                            }
                            // ****************************
                            default:
                                if (this._WarlockWorkAround(wsRequest))
                                    // Temporary fix. Request id need to be type "Warlock"
                                    this.handleWarlockResp(reqPayload, wsRequest);
                                else if ((wsRequest.Flags & Plegma.eMsgFlags.Request) != 0)
                                    this.handleUnknownReq(wsRequest.SyncId);
                                break;
                        }
                        break;
                    } //case Plegma.eWSMsgId.Api

                    // ------------------------------------
                    case Plegma.eWSMsgId.Warlock: {
                        this.handleWarlockResp(reqPayload, wsRequest);
                    } //case Plegma.eWSMsgId.Warlock

                    // ------------------------------------
                } // switch (wsRequest.Id)
            } catch (ex) {
                DebugEx.TraceError("Failed to handle WS request id:" + wsRequest.Id + "  SubId:" + wsRequest.SubId, ex);
                this.reportError(ErrorType.Msg, ErrorCode.MsgRx, ex.message);
            }
        }
        // ----------------------------------------------------
    }
    // --------------------------------------------------------------------------------------------------
    /**
     * Warlock response WORKAROUND
     * Will be removed, when PlegmaEntry is redeployed.
     */
    private _WarlockWorkAround(wsRequest: Plegma.WebSocketMsg): boolean {
        return isDefined(wsRequest.SyncId) && isDefined(this.syncReqCallbacks[wsRequest.SyncId]);
    }
    // --------------------------------------------------------------------------------------------------
    public Stop() {
        this.socket.close();
    }
    // --------------------------------------------------------------------------------------------------
    public SetConfigurations(wsConf: WSConf) {
        this.conf = wsConf;
    }
    // --------------------------------------------------------------------------------------------------
    public Pair() {
        DebugEx.TraceLog("Pairing request");

        let payload: Plegma.PairingNodeGetTokensRequest = {
            name: this.conf.ApplicationName,
            uuid: this.conf.ApplicationUUID,
            description: this.conf.ApplicationDescription,
            image: this.conf.ApplicationImage,

            RedirectUri: window.location.href,
            NoUUIDAuthentication: true,
            PairingCompletionInstructions: "",
            pathcss: "",
            PublicKey: null
        };

        this.sendMsg(
            Plegma.eWSMsgId.Pairing,
            PlegmaStatics.NodePairingConstants.s_GetTokensRequest,
            payload
        );
    }
    // --------------------------------------------------------------------------------------------------
    public SendPortStateGet(operation: Plegma.ePortStateOperation, portKeys?: string[]) {
        DebugEx.TraceVerbose("PortStateGet:", portKeys);

        let payload: Plegma.PortStateGet = {
            Operation: operation,
            PortKeys: [],
            SeqNo: 0
        };

        if (operation == Plegma.ePortStateOperation.SpecificKeys && isDefined(portKeys) && portKeys.length > 0) {
            _.forEach(portKeys, (portKey) => {
                payload.PortKeys.push(this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + portKey);
            })
        }

        this.syncReqId++;
        this.sendMsg(
            Plegma.eWSMsgId.Api,
            PlegmaStatics.PlegmaAPI.s_PortStateGet,
            payload,
            this.syncReqId,
            Plegma.eMsgFlags.Request
        );
    }
    // --------------------------------------------------------------------------------------------------
    public SendPortEvent(event: Plegma.PortEvent) {
        this.checkConnection();

        let payload: Plegma.PortEventMsg = {
            PortEvents: [],
            SeqNo: this.getNewPortEventSeqId(),
            INeedAck: true
        };

        // Find if is active
        let key = this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + event.PortKey;
        let isActive = !isDefined(this.activePortKeys) || isDefined(_.find(this.activePortKeys, (pkey) => pkey == key));
        if (isActive) {
            let eventCopy = _.cloneDeep(event);
            eventCopy.PortKey = key;

            // stringify if we need
            if (isObject(eventCopy.State))
                eventCopy.State = JSON.stringify(eventCopy.State);

            payload.PortEvents.push(eventCopy);

            this.sendMsg(
                Plegma.eWSMsgId.Api,
                PlegmaStatics.PlegmaAPI.s_PortEventMsg,
                payload
            );
        }
    }

    // --------------------------------------------------------------------------------------------------
    public SendPortEvents(events: Plegma.PortEvent[]) {
        this.checkConnection();

        let payload: Plegma.PortEventMsg = {
            PortEvents: [],
            SeqNo: this.getNewPortEventSeqId(),
            INeedAck: true
        };

        // Process all events
        _.forEach(events, (event) => {
            // Find if is active
            let key = this.conf.Keys.nodeKey + PlegmaStatics.PlegmaAPI.KeySeparator + event.PortKey;
            let isActive = !isDefined(this.activePortKeys) || isDefined(_.find(this.activePortKeys, (pkey) => pkey == key));
            if (isActive) {
                let eventCopy = _.cloneDeep(event);
                eventCopy.PortKey = key;

                // stringify if we need
                if (isObject(eventCopy.State))
                    eventCopy.State = JSON.stringify(eventCopy.State);

                payload.PortEvents.push(eventCopy);
            }
        });

        if (payload.PortEvents.length > 0)
            this.sendMsg(
                Plegma.eWSMsgId.Api,
                PlegmaStatics.PlegmaAPI.s_PortEventMsg,
                payload
            );
    }
    // --------------------------------------------------------------------------------------------------
    public SendWarlockReq(SubId: string, Payload: any, Callback?: (rsp: any) => void) {
        this.checkConnection();

        const syncId = this.getNewSyncId(Callback);

        this.sendMsg(
            Plegma.eWSMsgId.Api,
            SubId,
            Payload,
            syncId
        );
    }
    // --------------------------------------------------------------------------------------------------
    public SendThingGet() {
        let payload: Plegma.ThingsGet = {
            SeqNo: 0,
            Operation: Plegma.eThingsOperation.Get,
            Key: null,
            RevNum: 0,
            ThingKey: null
        };

        this.syncReqId++;
        this.sendMsg(
            Plegma.eWSMsgId.Api,
            PlegmaStatics.PlegmaAPI.s_ThingsGet,
            payload,
            this.syncReqId,
            Plegma.eMsgFlags.Request
        );
    }
    // --------------------------------------------------------------------------------------------------
    public SetThings(things: Plegma.Thing[], operation: Plegma.eThingsOperation, hierarchy: string = undefined) {
        // Replace the local things to the set on
        // If we are set only in supmodule we need to merge only the match keys
        if (IsNullOrEmpty(hierarchy)) {
            _.remove(this.things, (t) =>
                t.Hierarchy == hierarchy && // In the same hierarchy 
                _.findIndex(things, nt => nt.ThingKey == t.ThingKey) == -1 // but is not exist in the new list
            );

            _.forEach(things, (t) => {
                let oldTIndex = _.findIndex(this.things, (ot) => ot.ThingKey == t.ThingKey);
                if (oldTIndex > -1) {
                    this.things[oldTIndex] = t;
                } else {
                    this.things.push(t);
                }
            });
        } else {
            this.things = things;
        }

        // Update server with the new things
        this.syncReqId++;
        this.handleThingsGetSet(null, this.syncReqId, false, operation, hierarchy);
    }
    // --------------------------------------------------------------------------------------------------
}